import { Component, Inject, Input, LOCALE_ID } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { AdminRetailNotificationContactsDetailDialog } from '../admin-retail-notification-contacts-list/admin-retail-notification-contacts-list.component';
import { MessageService } from 'src/app/shared/message.service';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { UserService } from 'src/app/shared/user.service';
import { AuthenticationService } from 'src/app/core/authentication/shared/authentication.service';
import { RetailContactsService } from 'src/app/shared/retail-contacts.service';

const EMAIL_REGEX = /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/

@Component({
  selector: 'rpms-admin-retail-notification-contacts-detail',
  templateUrl: './admin-retail-notification-contacts-detail.component.html',
  styleUrl: './admin-retail-notification-contacts-detail.component.css'
})
export class AdminRetailNotificationContactsDetailComponent {
  @Input() id: string; 
  @Input() userId: string; 
  @Input() userIdsInList: string[]; 

  confirmationDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  mode: string;
  title: string;
  isManualEntry: boolean = false;
  isEditing: boolean = false;
  users = [];
  myUser;

  retailNotificationContactDetailForm = this._fb.group({
    radioButtonGroup: [''],
    userIds: [''],
    contactFirstName: [''],
    contactLastName: ['', Validators.required],
    contactPhone: [''],
    contactEmail: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
    active: [''],
  });

  constructor(
    private _fb: UntypedFormBuilder,
    private _retailContactsService: RetailContactsService,
    private _authenticationService: AuthenticationService,
    private _userService: UserService,
    private _messageService: MessageService,
    public dialogRef: MatDialogRef<AdminRetailNotificationContactsDetailDialog>,
    @Inject(LOCALE_ID) private locale: string) {
      this.myUser = this._authenticationService.getMyUser();
    }

    ngOnInit() {
      //console.log('init retail notification contacts detail');
      this.isEditing = (this.id != '0');
      this.title = this.id == '0'? 'Add Retail Notification Contact' : 'Edit Retail Notification Contact';
      this.retailNotificationContactDetailForm.get('radioButtonGroup').setValue('enter');
      this.isManualEntry = true;
      if (this.isEditing){ 
        if (this.userId !== null) { //was chosen from user list
          this.retailNotificationContactDetailForm.get('contactFirstName').disable();
          this.retailNotificationContactDetailForm.get('contactLastName').disable();
          this.retailNotificationContactDetailForm.get('contactPhone').disable();
          this.retailNotificationContactDetailForm.get('contactEmail').disable();
        }
        else {
          this.retailNotificationContactDetailForm.get('contactFirstName').enable();
          this.retailNotificationContactDetailForm.get('contactLastName').enable();
          this.retailNotificationContactDetailForm.get('contactPhone').enable();
          this.retailNotificationContactDetailForm.get('contactEmail').enable();
        }
        this.getRetailContactById(this.id);
      }
      else{ 
        this.retailNotificationContactDetailForm.patchValue({ active: true });
      }
    }

    getRetailContactById(retailContactId){
      this._retailContactsService.getRetailContactById(retailContactId)
        .subscribe(
          (data: any) => {
            let contact = data.user != null ? data.user : data;
            this.retailNotificationContactDetailForm.patchValue({
              active: data.active,
              contactFirstName: contact.firstName,
              contactLastName: contact.lastName,
              contactPhone: contact.phoneNumber,
              contactEmail: contact.emailAddress || contact.email,
            });
  
          },
          (response) => {
            this._messageService.onFailure('Failed to get retail contacts.', response);
          })
    }

    onRadioButtonChange(value){
      this.isManualEntry = (value == 'enter');
      if (this.isManualEntry){
        //this.retailNotificationContactDetailForm.get('contactFirstName').setValidators([Validators.required]);
        this.retailNotificationContactDetailForm.get('contactLastName').setValidators([Validators.required]);
        this.retailNotificationContactDetailForm.get('contactEmail').setValidators([Validators.required, Validators.pattern(EMAIL_REGEX)]);
        this.title = this.id == '0'? 'Add Retail Notification Contact' : 'Edit Retail Notification Contact';
      } 
      else{
        //this.retailNotificationContactDetailForm.get('contactFirstName').clearValidators();
        this.retailNotificationContactDetailForm.get('contactLastName').clearValidators();
        this.retailNotificationContactDetailForm.get('contactEmail').clearValidators();
        this.title = this.id == '0'? 'Add or Remove' : 'Edit';
        if (this.id == '0'){ //create
          if (this.userIdsInList && this.userIdsInList.length > 0){
            this.title = 'Add or Remove Retail Notification Contacts';
          }
          else{
            this.title = 'Add Retail Notification Contacts';
          }
        }
        else{ //edit
          this.title = 'Edit Retail Notification Contact';
        }
        if (this.users.length < 1) this.getUsers();
      }
      this.retailNotificationContactDetailForm.get('contactFirstName').updateValueAndValidity();
      this.retailNotificationContactDetailForm.get('contactLastName').updateValueAndValidity();
      this.retailNotificationContactDetailForm.get('contactEmail').updateValueAndValidity();
    }

    getUsers(){
      this._userService.getActiveManageableUsers()
        .subscribe(
          (data: any) => {
            this.users = data.sort((a, b) => (a.fullName.toLowerCase() > b.fullName.toLowerCase()) ? 1 : -1);
            this.retailNotificationContactDetailForm.get('userIds').setValue(this.userIdsInList);
          },
          (response) => {
            this._messageService.onFailure('Failed to get users.', response);
          })
    }

    onSubmit(){
      //Users with hasReadOnlyRestriction set to true should not even be allowed to invoke this action because it is hidden or disabled in the UI.
      //This is for extra protection. The back end will also be protected.
      if (this.myUser.hasReadOnlyRestriction){
        this._messageService.alertReadOnlyRestriction();
        return;
      }

      //console.log(this.retailNotificationContactDetailForm.value);

      if (this.isManualEntry){
        let contact = {
          userId: this.userId,
          firstName: this.retailNotificationContactDetailForm.get('contactFirstName').value,
          lastName: this.retailNotificationContactDetailForm.get('contactLastName').value,
          phoneNumber: this.retailNotificationContactDetailForm.get('contactPhone').value,
          emailAddress: this.retailNotificationContactDetailForm.get('contactEmail').value,
          active: this.retailNotificationContactDetailForm.get('active').value
        }

        //if this contact was picked from a user account, clear values per JL validations
        if (contact.userId != null) {
          contact.firstName = null;
          contact.lastName = null;
          contact.phoneNumber = null;
          contact.emailAddress = null;
        }

        if (this.isEditing) this.updateContact(contact);
        else this.createContact(contact);
      }
      else {
        let userIds = this.retailNotificationContactDetailForm.get('userIds').value;
        this.addOrRemoveContacts(userIds);
      }
    }

    // getUserFromId(userId){
    //   return this.users.find(u => u.userId === userId);
    // }

    createContact(contact){
      this._retailContactsService.createRetailContact(contact)
      .subscribe(
        () => {
          this.dialogRef.close('reload');
          this._messageService.onSuccess('Successfully created retail contact');
        },
        (response) => {
          this._messageService.onFailure('Failed to create retail contact.', response)
        }
      );
    }

    updateContact(contact){
      //console.log(contact);
      this._retailContactsService.updateRetailContact(this.id, contact)
      .subscribe(
        () => {
          this.dialogRef.close('reload');
          this._messageService.onSuccess('Successfully updated retail contact');
        },
        (response) => {
          this._messageService.onFailure('Failed to update retail contact.', response)
        }
      );
    }

    addOrRemoveContacts(userIds){
      //console.log(userIds);
      this._retailContactsService.setUserAccountBasedRetailContacts(userIds)
      .subscribe(
        () => {
          this.dialogRef.close('reload');
          this._messageService.onSuccess('Successfully updated retail contacts');
        },
        (response) => {
          this._messageService.onFailure('Failed to update retail contacts.', response)
        }
      );
    }

    closeDialog(){
      this.dialogRef.close();
    }
  
}
